/**
 * Impersonation start-failure resolution (#1323).
 *
 * user-service refuses `POST /users/impersonation/admin/start` with
 *   { success:false, error, message, code: 'IMPERSONATION_*' }
 * and a status bound to the code. The shared api client rejects with the
 * parsed body (NOT the axios error — `error.response` is never present), so
 * anything that reads `error.response?.data` silently falls through to the
 * generic "Failed to start impersonation". This module is the single place
 * that turns whatever was thrown into the reason the admin should see.
 */

export const IMPERSONATION_ERROR_CODES = [
  'IMPERSONATION_TARGET_NOT_FOUND',
  'IMPERSONATION_TARGET_IS_ADMIN',
  'IMPERSONATION_TARGET_INACTIVE',
  'IMPERSONATION_TARGET_IN_USE',
  'IMPERSONATION_ALREADY_ACTIVE',
  'IMPERSONATION_SELF',
  'IMPERSONATION_FORBIDDEN',
  'IMPERSONATION_INVALID_REQUEST',
  'IMPERSONATION_START_FAILED',
] as const

export type ImpersonationErrorCode = (typeof IMPERSONATION_ERROR_CODES)[number]

/**
 * Client-side reasons by code. Used as the i18n default for
 * `toasts:error.impersonation.<CODE>` and as the fallback when the backend
 * sends a code without a message.
 */
export const IMPERSONATION_ERROR_REASONS: Record<ImpersonationErrorCode, string> = {
  IMPERSONATION_TARGET_NOT_FOUND: 'Cannot impersonate: the target user was not found.',
  IMPERSONATION_TARGET_IS_ADMIN: 'Cannot impersonate: the target user is an administrator.',
  IMPERSONATION_TARGET_INACTIVE: "Cannot impersonate: the target user's account is not active.",
  IMPERSONATION_TARGET_IN_USE:
    'Cannot impersonate: this user is already being impersonated by another administrator.',
  IMPERSONATION_ALREADY_ACTIVE:
    'Cannot start impersonation while already impersonating a user. Please stop the current impersonation first.',
  IMPERSONATION_SELF: 'Cannot impersonate: you cannot impersonate your own account.',
  IMPERSONATION_FORBIDDEN: 'You do not have permission to impersonate users.',
  IMPERSONATION_INVALID_REQUEST: 'Invalid impersonation request.',
  IMPERSONATION_START_FAILED: 'Failed to start impersonation',
}

export const GENERIC_IMPERSONATION_START_ERROR = 'Failed to start impersonation'

export interface ImpersonationErrorInfo {
  code?: string
  message?: string
}

/** Minimal `t` contract so callers can pass react-i18next's `t` directly. */
type Translate = (key: string, defaultValue: string) => string

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null

const nonEmpty = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim().length > 0 ? v : undefined

export const isImpersonationErrorCode = (code: unknown): code is ImpersonationErrorCode =>
  typeof code === 'string' && (IMPERSONATION_ERROR_CODES as readonly string[]).includes(code)

/**
 * Pull `{ code, message }` out of whatever the api layer rejected with.
 * Handles, in order: the raw backend body (what apiClient actually throws),
 * an axios-shaped error (`err.response.data`) for callers that bypass the
 * shared client, and nothing else — a bare `Error` (network / timeout /
 * `unwrap()` failure) carries no admin-facing reason and resolves to the
 * generic text.
 */
export function extractImpersonationError(err: unknown): ImpersonationErrorInfo {
  if (!isRecord(err)) return {}

  // axios-shaped → its parsed body; a bare Error (network/timeout) has no
  // admin-facing reason; anything else is treated as the body itself.
  const body =
    isRecord(err.response) && isRecord(err.response.data)
      ? err.response.data
      : err instanceof Error
        ? undefined
        : err
  if (!isRecord(body)) return {}

  const code = nonEmpty(body.code)
  // `error` and `message` both carry the reason on impersonation refusals;
  // older/other envelopes may set only one of them.
  const message = nonEmpty(body.message) ?? nonEmpty(body.error)
  if (!code && !message) return {}

  return { code, message }
}

/**
 * Resolve the toast/alert text for a failed impersonation start:
 *   1. known code + backend message  → the backend's reason (most specific,
 *      e.g. carries the target's concrete status)
 *   2. known code, no message        → localized client reason for that code
 *   3. unknown/no code, any message  → the backend's message as-is
 *   4. otherwise                     → generic "Failed to start impersonation"
 */
export function resolveImpersonationStartError(err: unknown, t: Translate): string {
  const { code, message } = extractImpersonationError(err)

  if (isImpersonationErrorCode(code)) {
    return message ?? t(`error.impersonation.${code}`, IMPERSONATION_ERROR_REASONS[code])
  }
  if (message) return message
  return t('errors.impersonationStartFailed', GENERIC_IMPERSONATION_START_ERROR)
}
